import { isShopify, type Platform } from '../../context/env-context';
import type { Product, Variant } from './types/product';

type OptionValue = Variant['option_values'][number];

function optimiseImageSearch(url: string, platform?: Platform): string {
  if (isShopify(platform)) {
    const imageUrl = new URL(url);
    imageUrl.searchParams.set('width', '200');
    imageUrl.searchParams.set('height', '200');
    return imageUrl.toString();
  }

  if (platform === 'wix') {
    return url.replace(/\/v1\/(fit|fill)\/w_\d+,h_\d+(,[^/]*)?\//, `/v1/fit/w_200,h_200,q_90/`);
  }

  return url;
}

export function findImage(product: Product | undefined, platform?: Platform): string {
  const imageMetadata = product?.metadata?.find(
    (m) => m.key === 'images' && m.type === 'array_of_string',
  );

  const imageUrl = Array.isArray(imageMetadata?.value) ? imageMetadata.value[0] : undefined;

  if (imageUrl) {
    return optimiseImageSearch(String(imageUrl), platform);
  }

  return '';
}

export function findVariantImage(variant: Variant | undefined, platform?: Platform): string {
  const imageUrl = variant?.cart_image_url;

  if (imageUrl) {
    return optimiseImageSearch(imageUrl, platform);
  }

  return '';
}

export function findMetaDataEntry(
  metadata: Product['metadata'] | Variant['metadata'],
  namespace: string,
  key: string,
) {
  return metadata?.find((m) => m.namespace === namespace && m.key === key);
}

export function findOptions(variant: Variant): string {
  return (
    variant.metadata
      .filter((m) => m.namespace === 'sparklayer_option_values')
      .map((m) => (typeof m.value === 'string' ? m.value : String(m.value)))
      .join(', ') ?? ''
  );
}

export function resolveOptionName(
  product: Product,
  variant: Variant | undefined,
  optionValue: OptionValue,
): Record<string, string> {
  const groupMetadataEntry = findMetaDataEntry(
    product.metadata,
    optionValue.group_metadata_namespace,
    optionValue.group_metadata_key,
  );

  const valueMetadataEntry = findMetaDataEntry(
    variant?.metadata,
    optionValue.value_metadata_namespace,
    optionValue.value_metadata_key,
  );

  const key =
    typeof groupMetadataEntry?.value === 'string'
      ? groupMetadataEntry.value
      : optionValue.group_metadata_key;
  const value =
    typeof valueMetadataEntry?.value === 'string'
      ? valueMetadataEntry.value
      : optionValue.value_metadata_key;

  return {
    [key]: value,
  };
}
